import {
  AssociationPosition,
  AssociationTeam,
  CommunicationRecord,
  CommunityAssociation,
  GroupAssociation,
  Opportunity,
  OpportunityParticipant,
  OpportunityReport,
  Organization,
  Status,
  TimeReport,
  User,
} from "@entities";
import { extender } from "@shared/backend/class-extender";
import HyperEntity from "@shared/backend/hyper-entity.decorator";
import {
  ACTIVITY_STATUS,
  requestedOpportunitiesWhere,
} from "@shared/helpers/constants";
import { JsonField } from "@shared/helpers/custom-fields";
import { sqlRelationalField } from "@shared/helpers/sql-relations";
import { OrganizationRoles } from "@shared/Roles";
import { Fields, Relations, remult } from "remult";
import { sqlRelations } from "remult/internals";
import { isAdminsOrCurrentUser } from "../../helpers/include-in-api.function";
import { AssociationEntityConfig } from "./config";
import { AssociationFilters } from "./filters";
import { AssociationMethods } from "./methods";

@HyperEntity<Association>("association", AssociationEntityConfig)
export class Association extends extender(
  AssociationFilters,
  AssociationMethods,
) {
  @Fields.uuid()
  id = "";

  @JsonField<Association, Association["roles"]>({
    validate: (_, { value }) => {
      if (value === undefined || value === null) return true;
      if (!Array.isArray(value)) return false;
      return value.every((v) => Object.values(OrganizationRoles).includes(v));
    },
  })
  // Roles must be assigned explicitly by registration flows.
  // Defaulting to VOLUNTEER caused non-volunteer users (e.g. requesters / group members)
  // to appear in volunteer-only surfaces.
  roles: OrganizationRoles[] = [];

  @Fields.string({ dbName: "user_id" })
  userId = "";

  @Fields.string({ dbName: "organization_id" })
  organizationId = "";

  @Relations.toOne<Association, Organization>(
    () => Organization,
    "organizationId",
  )
  organization?: Organization;

  @Relations.toMany<Association, AssociationPosition>(
    () => AssociationPosition,
    "associationId",
  )
  userPositions?: AssociationPosition[];

  @Fields.string({
    dbName: "status_id",
    allowNull: true,
    valueConverter: {
      toDb: (val) => (val === "" ? null : val),
      fromDb: (val) => (val == null ? "" : val),
    },
  })
  statusId = "";

  @Relations.toMany<Association, CommunicationRecord>(
    () => CommunicationRecord,
    "associationId",
  )
  communicationRecords?: CommunicationRecord[];

  @Relations.toOne(() => Status, {
    caption: "סטטוס",
    allowNull: true,
    field: "statusId",
  })
  status?: Status;

  @Relations.toOne<Association, User>(() => User, "userId")
  user?: User;

  @Fields.string({
    sqlExpression: sqlRelationalField<Association>(
      (fields: any) => fields.user?.name() || "",
    ),
  })
  userName = "";

  @Relations.toMany<Association, CommunityAssociation>(
    () => CommunityAssociation,
    {
      fields: {
        associationId: "id",
      },
    },
  )
  communities: CommunityAssociation[] = [];

  @Relations.toMany<Association, GroupAssociation>(() => GroupAssociation, {
    fields: {
      associationId: "id",
    },
  })
  groups: GroupAssociation[] = [];

  @Relations.toMany<Association, Opportunity>(() => Opportunity, {
    fields: {
      associationRequestedId: "id",
    },
    caption: "משימות משויכות",
  })
  requestedOpportunities: Opportunity[] = [];

  @Fields.number({
    dbName: "requested_opportunities_count",
    caption: "מספר בקשות משויכות",
    sqlExpression: async () => {
      if (!remult.user?.organizationId) return "0";
      return sqlRelations(Association).requestedOpportunities.$count(
        requestedOpportunitiesWhere,
      );
    },
  })
  requestedOpportunitiesCount = 0;

  @Relations.toMany<Association, AssociationTeam>(() => AssociationTeam)
  teams: AssociationTeam[] = [];

  @JsonField<Association, Association["preferredPositions"]>({
    dbName: "preferred_positions",
    caption: "העדפות",
  })
  preferredPositions: string[] = [];

  @Fields.literal(() => ACTIVITY_STATUS, {
    dbName: "activity_status",
    caption: "סטטוס פעילות",
  })
  activityStatus: (typeof ACTIVITY_STATUS)[number] = "ACTIVE";

  @Relations.toMany<Association, OpportunityReport>(() => OpportunityReport, {
    fields: {
      associationId: "id",
    },
  })
  reports: OpportunityReport[] = [];

  @Relations.toMany<Association, TimeReport>(() => TimeReport, {
    fields: {
      associationId: "id",
    },
  })
  timeReports: TimeReport[] = [];

  @Relations.toMany<Association, OpportunityParticipant>(
    () => OpportunityParticipant,
    {
      fields: {
        associationId: "id",
      },
    },
  )
  opportunities: OpportunityParticipant[] = [];

  @JsonField<Association, Association["personalDetails"]>({
    dbName: "personal_details",
    allowedKeys: ["name", "email", "numberID"],
    caption: "פרטים אישיים",
  })
  personalDetails?: {
    name: string;
    email: string;
    numberID: string;
  };

  @Fields.string({ includeInApi: isAdminsOrCurrentUser, caption: "הערות לשימוש פנימי" })
  comments = "";

  @Fields.number({
    dbName: "scholarships_hours_number",
    caption: "שעות למלגאים",
  })
  scholarshipsHoursNumber = 0;

  @Fields.boolean({
    dbName: "allowed_ads",
  })
  allowedAds = false;

  @Fields.boolean({ dbName: "is_deleted" })
  isDeleted = false;

  @Fields.createdAt({
    caption: "נוצר/ה בתאריך",
    dbName: "created_at",
    valueConverter: {
      displayValue: (val) =>
        val ? new Date(val || "")?.toLocaleDateString("he") : "-",
    },
  })
  createdAt: Date = new Date();

  @Fields.updatedAt({ dbName: "updated_at" })
  updatedAt: Date = new Date();

  @Fields.date({
    dbName: "last_relevant_sms_at",
    caption: "תאריך שליחת SMS למתנדבים רלוונטיים",
    allowNull: true,
  })
  lastRelevantSmsAt?: Date;

  @Fields.date({
    dbName: "status_updated_at",
    caption: "תאריך עדכון סטטוס",
    allowNull: true,
  })
  statusUpdatedAt?: Date;
}
